import {
  Box,
  styled,
  Stack,
  Typography,
  Divider,
  IconButton,
  Alert,
} from "@mui/material";
import { motion, AnimatePresence } from "motion/react";
import AiTextField from "./AiTextField";
import AiResponse from "./AiResponse";
import {
  FLYOUT_WIDTH,
  LEFT_BAR_WIDTH,
  TOP_BAR_HEIGHT,
} from "features/leftMenu/constants";
import { useSelector } from "react-redux";
import { leftMenuSelector } from "Selectors/leftMenuSelector";
import { useEffect, useState, useCallback, useRef } from "react";
import Loading from "Components/Loading/Loading";
import { Close } from "@mui/icons-material";
import { useAiContext } from "../providers/AiProvider";
import { ApiAIPromptStatus } from "@incendium/api";
import { useDebounce } from "react-use";

const Wrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isPinned",
})<{ $isPinned: boolean }>(({ theme, $isPinned }) => ({
  backgroundColor: theme.palette.background.default,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "fixed",
  top: TOP_BAR_HEIGHT,
  right: 0,
  bottom: 0,
  left: $isPinned ? LEFT_BAR_WIDTH + FLYOUT_WIDTH + 1 : LEFT_BAR_WIDTH,
  zIndex: 1000,
}));

const UserBlock = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  alignSelf: "flex-end",
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  maxWidth: "80%",
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  borderRadius: "50%",
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.background.default,
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
    },
  },
}));

function AiChatModal() {
  const {
    setActive,
    setCurrentQuestion,
    messages,
    clearCurrentSession,
    isConnecting,
    streamingError,
    isStreaming,
    hasFailedPrompt,
  } = useAiContext();

  const { isFlyoutPinned: isFlyoutPinnedOnly, isSecondaryAvailable } =
    useSelector(leftMenuSelector);
  const isFlyoutPinned = isFlyoutPinnedOnly || isSecondaryAvailable;
  const [loading, setLoading] = useState(true);
  const messagesRef = useRef<HTMLDivElement>(null);
  useDebounce(
    () => {
      if (loading || isConnecting) return;
      const el = messagesRef.current;
      if (!el) return;
      // Wait for DOM layout, then scroll
      // only scroll if scrollHeight is greater than the height of the element
      if (el.scrollHeight > el.clientHeight) {
        requestAnimationFrame(() => {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        });
      }
    },
    300,
    [
      messages.length,
      isConnecting,
      loading,
      messages[messages.length - 1]?.response?.length,
    ],
  );

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  const handleClose = useCallback(() => {
    setActive(false);
    setCurrentQuestion("");
    clearCurrentSession();
  }, [setActive, setCurrentQuestion, clearCurrentSession]);

  return (
    <Wrapper $isPinned={isFlyoutPinned} p={4}>
      <CloseButton onClick={handleClose}>
        <Close />
      </CloseButton>
      <Stack
        justifyContent="flex-end"
        alignItems="center"
        sx={{ width: "100%", maxWidth: 1200, height: "100%" }}
        spacing={6}
      >
        <Stack
          px={2}
          ref={messagesRef}
          sx={{
            flex: 1,
            minHeight: 0,
            width: "calc(100% + 32px)",
            overflowY: loading ? "hidden" : "auto",
            overflowX: "hidden",
            scrollbarGutter: "stable",
          }}
          spacing={4}
        >
          {loading ? (
            <Loading fullHeight key="loading" />
          ) : (
            <AnimatePresence>
              {messages.length === 0 && !isConnecting ? (
                <motion.div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 200,
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography variant="h6" color="text.secondary">
                    Ask me anything
                  </Typography>
                </motion.div>
              ) : (
                <>
                  {messages.map((message, index) => (
                    <motion.div
                      style={{
                        display: "flex",
                        width: "100%",
                        justifyContent:
                          message.userInput !== "" ? "flex-end" : "flex-start",
                      }}
                      key={
                        message.id +
                        (message.userInput !== ""
                          ? "user"
                          : message.status ===
                              ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
                            ? "pending"
                            : "ai")
                      }
                      initial={{ opacity: 0, scale: 0.8, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{
                        duration: 0.5,
                        ease: "easeOut",
                      }}
                    >
                      {message.userInput !== "" ? (
                        <UserBlock>
                          <Typography>{message.userInput}</Typography>
                        </UserBlock>
                      ) : (
                        <Box sx={{ width: "80%" }}>
                          <AiResponse message={message} />
                        </Box>
                      )}
                    </motion.div>
                  ))}

                  {/* Show loading indicator when connecting */}
                  {(isConnecting || isStreaming) && (
                    <motion.div
                      style={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "flex-start",
                      }}
                      initial={{ opacity: 0, scale: 0.8, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{
                        duration: 0.5,
                        ease: "easeOut",
                      }}
                    >
                      <Box sx={{ width: "90%" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            padding: 2,
                            borderRadius: 2,
                          }}
                        >
                          <Loading />
                          <Typography variant="body2" color="text.secondary">
                            {isConnecting
                              ? "Connecting to AI..."
                              : "Generating response..."}
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  )}

                  {/* Show error message if streaming failed */}
                  {streamingError && (
                    <motion.div
                      style={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "flex-start",
                      }}
                      initial={{ opacity: 0, scale: 0.8, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{
                        duration: 0.5,
                        ease: "easeOut",
                      }}
                    >
                      <Box sx={{ width: "90%" }}>
                        <Alert severity="error">{streamingError}</Alert>
                      </Box>
                    </motion.div>
                  )}
                </>
              )}
            </AnimatePresence>
          )}
        </Stack>
        <Divider />
        {hasFailedPrompt ? (
          <motion.div
            style={{ width: "100%" }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: 0.3,
              ease: "easeOut",
            }}
          >
            <Typography variant="h4" align="center" color="text.secondary">
              Chat Ended
            </Typography>
          </motion.div>
        ) : (
          <motion.div
            style={{ width: "100%" }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: 0.3,
              ease: "easeOut",
            }}
          >
            <AiTextField />
          </motion.div>
        )}
      </Stack>
    </Wrapper>
  );
}

export default AiChatModal;
